import React, { Fragment } from "react";
import {
  Spin,
  Row,
  Col,
  Card as AntdCard,
  Statistic,
  Divider,
  Tag,
  Table,
} from "antd";
import Card from "../../components/Card";
import { Bar, Pie } from "react-chartjs-2";
import { User } from "../../helpers/shared-interfaces";
import { RouteComponentProps } from "react-router-dom";

interface ChartDataSet {
  label: string;
  backgroundColor?: string;
  borderColor?: string;
  data: number[];
}

interface ChartData {
  labels: string[];
  datasets: ChartDataSet[];
}

export interface ObjectLiteral {
  [key: string]: number[];
}

const barChartOptions = {
  showScale: true,
  pointDot: true,
  showLines: true,

  legend: {
    display: true,
    position: "bottom",
  },

  scales: {
    yAxes: [
      {
        ticks: {
          beginAtZero: true,
          min: 0,
        },
      },
    ],
  },
};

const data = [
  {
    id: "1",
    date: "12/11/2020",
    status: "matched",
  },
  {
    id: "2",
    date: "13/12/2020",
    status: "unmatched",
  },
  {
    id: "3",
    date: "15/12/2020",
    status: "processing",
  },
];

export const columns = [
  {
    title: "Id",
    dataIndex: "id",
    key: "id",
  },
  {
    title: "Date",
    dataIndex: "date",
    key: "date",
  },
  {
    title: "Status",
    key: "status",
    dataIndex: "status",
    render(text: string): JSX.Element {
      let color;
      if (text === "unmatched") {
        color = "#fcb900";
      } else if (text === "matched") {
        color = "#87d068";
      } else {
        color = "#add8e6";
      }
      return (
        <Tag color={color} key={text}>
          {text}
        </Tag>
      );
    },
  },
];

const barData = {
  labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
  datasets: [
    {
      label: "Email",
      data: [33, 53, 85, 41, 44, 65],
      fill: true,
      backgroundColor: "rgb(203, 172, 18)",
      borderColor: "rgb(203, 172, 18)",
    },
    {
      label: "WhatsApp",
      data: [33, 25, 35, 51, 54, 76],
      fill: true,
      backgroundColor: "rgb(37, 191, 88)",
      borderColor: "rgb(37, 191, 88)",
    },
    {
      label: "Phone Call",
      data: [33, 25, 35, 51, 54, 76],
      fill: true,
      backgroundColor: "rgb(203, 18, 191)",
      borderColor: "rgb(203, 18, 191)",
    },
    {
      label: "WalkIn",
      data: [33, 25, 35, 51, 54, 76],
      fill: true,
      backgroundColor: "rgb(229, 54, 10)",
      borderColor: "rgb(229, 54, 10)",
    },
  ],
};

const pieChartOptions = {
  title: "Tickets",

  legend: {
    display: true,
    position: "right",
  },
};

const customersPieData = {
  labels: ["Active", "Paying"],
  datasets: [
    {
      label: "Active and Paying Customers",
      backgroundColor: ["#B21F00", "#C9DE00", "#2FDE00", "#00A6B4"],
      hoverBackgroundColor: ["#501800", "#4B5000", "#175000", "#003350"],
      data: [65, 59],
    },
  ],
};

export interface dashboard {
  department: string;
  component: typeof React.Component;
}

export interface dashboardSelectorProps {
  dashboards: dashboard[];
  user: User;
}

interface State {
  viewToRender: string;
  transformedData: ChartData;
  isLoading: boolean;
  wonOpportunitiesCount: number;
  inProgressOpportunitiesCount: number;
  prospectiveOpportunitiesCount: number;
}

interface Props extends RouteComponentProps {
  user: User;
}

export function DealsTable(): JSX.Element {
  return (
    <Fragment>
      <p>Matched and Unmatched Deals</p>
      <Table columns={columns} dataSource={data} />
    </Fragment>
  );
}

export function PaymentsView(): JSX.Element {
  return (
    <Fragment>
      <Row>
        <p>Payments</p>
      </Row>

      <Row
        gutter={16}
        style={{
          marginBottom: "17px",
        }}
      >
        <Col span={6}>
          <AntdCard>
            <Statistic title="Identified" value={120} />
          </AntdCard>
        </Col>
        <Col span={6}>
          <AntdCard>
            <Statistic title="Unidentified" value={110} />
          </AntdCard>
        </Col>
        <Col span={6}>
          <AntdCard>
            <Statistic title="Unidentified" value={50} />
          </AntdCard>
        </Col>
        <Col span={6}>
          <AntdCard>
            <Statistic title="Matched/Unmatched" value={60} suffix="/40" />
          </AntdCard>
        </Col>
      </Row>
    </Fragment>
  );
}

export function InteractionsView(): JSX.Element {
  return (
    <Fragment>
      <Row>
        <p>Interactions</p>
      </Row>
      <Row gutter={16}>
        <Col span={24}>
          <AntdCard>
            <p>Total Weekly Interactions</p>
            <Bar
              data={barData}
              width={20}
              height={5}
              options={barChartOptions}
            />
          </AntdCard>
        </Col>
      </Row>
    </Fragment>
  );
}

export function SchedulesView(): JSX.Element {
  return (
    <Fragment>
      <Row>
        <p>Schedules</p>
      </Row>
      <Row gutter={16}>
        <Col span={24}>
          <AntdCard>
            <p>Total Schedules</p>
            <Bar
              data={barData}
              width={20}
              height={5}
              options={barChartOptions}
            />
          </AntdCard>
        </Col>
      </Row>
    </Fragment>
  );
}

export function RedemptionsCustomersView(): JSX.Element {
  return (
    <Fragment>
      <Row>
        <p>Redemptions</p>
      </Row>

      <Row
        gutter={16}
        style={{
          marginBottom: "17px",
        }}
      >
        <Col span={4}>
          <AntdCard>
            <Statistic
              title="Received in Week/Month"
              value={120}
              suffix="/230"
            />
          </AntdCard>
        </Col>
        <Col span={4}>
          <AntdCard>
            <Statistic title="Processed within Sla" value={50} />
          </AntdCard>
        </Col>
        <Col span={4}>
          <AntdCard>
            <Statistic title="Processed beyond Sla" value={60} />
          </AntdCard>
        </Col>
        <Col span={12}>
          <AntdCard>
            <p>Customers</p>
            <Pie
              data={customersPieData}
              width={20}
              height={5}
              options={pieChartOptions}
            />
          </AntdCard>
        </Col>
      </Row>
    </Fragment>
  );
}

export default class ClientServiceDashboard extends React.Component<
  Props,
  State
> {
  _isMounted = false;
  constructor(props: Readonly<Props>) {
    super(props);
    this.state = {
      isLoading: false,
      viewToRender: "All Actions",
      transformedData: { labels: [], datasets: [] },
      wonOpportunitiesCount: 0,
      inProgressOpportunitiesCount: 0,
      prospectiveOpportunitiesCount: 0,
    };
  }

  render(): JSX.Element | null | typeof React.Component | undefined {
    const { isLoading } = this.state;
    return (
      <Spin spinning={isLoading}>
        <Card>
          <Fragment>
            <RedemptionsCustomersView />
            <Divider />
            <PaymentsView />
            <Divider />
            <SchedulesView />
            <Divider />
            <InteractionsView />
            <Divider />
            <DealsTable />
          </Fragment>
        </Card>
      </Spin>
    );
  }
}
